/**
 * Non-component helpers for the form shells. Split out of `formShell.tsx` so
 * that file exports only components — mixing the two breaks React Fast Refresh
 * for every consumer of the shells.
 */
import { Children, isValidElement, type ReactNode } from 'react';
import { FormShellBody, FormShellFooter } from './formShell';

/**
 * Pulls the body and footer slots out of `children` by reference identity, so
 * the consumer can compose them in any order and mix in other children (which
 * are silently ignored).
 */
export function findSlots(children: ReactNode) {
  const childArray = Children.toArray(children);
  return {
    body: childArray.find((child) => isValidElement(child) && child.type === FormShellBody),
    footer: childArray.find((child) => isValidElement(child) && child.type === FormShellFooter),
  };
}

/**
 * Notistack toasts render in a body-level portal, so clicks on them count as
 * pointer-down-outside and would otherwise dismiss the shell. Predicate copied
 * verbatim from `AddArtifactsModal.tsx:147–154`.
 */
export function guardNotistackDismissal(event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  const target = event.target as HTMLElement | null;
  if (target?.closest('[class*="notistack"]')) {
    event.preventDefault();
  }
}
